import { useCallback, useEffect, useRef, useState } from 'react';
import DamageHitSplat from './DamageHitSplat';
import { createGuardianStrikeScene } from './guardianStrikeScene';
import { DIE_DRILL_AXIS, getDieDrillAngle } from './dieDrill';
import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import { observeElementSize } from '../../utils/observeElementSize';
import { formatDieValue, getDie } from './dice';
import { paintDieFaceSkin } from './dieFaceSkin';
import {
  getLabelContext,
  createLabelTexture,
  updateLabelTexture,
} from './labelTextures';
import { getCoreDieAppearance } from './coreDieAppearance';
import { createCoreDieScene } from './coreDieScene';
import { createAttackStyleAuraScene } from './attackStyleAuraScene';
import CoreDieFallback from './CoreDieFallback';
import { disposeSceneResources } from './sceneResources';
import { BOSS_MAX_HP } from './boss';
import { createCoreModel } from './coreModel';
import { createCoreLaser, getCoreLaserDuration } from './coreLaser';
import { CORE_APPEARANCE, getCoreAppearance } from './coreAppearance';
import {
  CORE_PHASE_ORDER,
  CORE_PHASE_RANK as CORE_PHASE_LEVEL,
  getCoreVisualState,
} from './corePhases';
import './CoreLivingHeart.css';
import {
  CORE_SPLIT_DURATION,
  CORE_EXPLOSION_DURATION,
  createCoreDestruction,
} from './coreDestruction';

const CORE_HOME = new THREE.Vector3(0, 0.8, 0);
const CORE_INTENT_TELL_COLORS = {
  'Static barrage': 0x5af5ff,
  Worldbreaker: 0xffb45e,
  'Siphon pulse': 0xf065ff,
};
const CORE_PHASE_TRANSITION_SECONDS = 2.4;

const formatRoll = formatDieValue;

const toCssColor = (color) =>
  typeof color === 'number' ? `#${color.toString(16).padStart(6, '0')}` : color;

const paintNumber = (context, width, height, value, color, dieType) => {
  const cssColor = toCssColor(color);
  const isDarkNumber =
    typeof color === 'number' &&
    ((color >> 16) & 0xff) * 0.299 +
      ((color >> 8) & 0xff) * 0.587 +
      (color & 0xff) * 0.114 <
      70;
  context.clearRect(0, 0, width, height);
  paintDieFaceSkin(context, width, height, dieType, cssColor);
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const fontSize = Math.min(
    224,
    Math.floor(560 / (String(value).length * 0.62))
  );
  context.font = `900 ${fontSize}px monospace`;
  context.shadowColor = cssColor;
  context.shadowBlur = 28;
  context.fillStyle = cssColor;
  context.fillText(value, width / 2, height / 2);
  context.shadowBlur = 5;
  context.lineWidth = 5;
  context.strokeStyle = isDarkNumber ? '#000000' : '#ffffffcc';
  context.strokeText(value, width / 2, height / 2);
};

const makeNumberTexture = (value, color, dieType) => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const context = getLabelContext(canvas);
  if (!context) return null;
  const texture = createLabelTexture(canvas);
  updateLabelTexture(texture, value, color, dieType, paintNumber);
  return texture;
};

const updateNumberTexture = (texture, value, color, dieType) => {
  updateLabelTexture(texture, value, color, dieType, paintNumber);
};

const addStars = (scene) => {
  const count = 360;
  const positions = new Float32Array(count * 3);
  for (let index = 0; index < count; index += 1) {
    const angle = index * 2.39996;
    const radius = 4 + ((index * 31) % 120) / 10;
    positions[index * 3] = Math.cos(angle) * radius;
    positions[index * 3 + 1] = ((index * 17) % 90) / 10 - 3.2;
    positions[index * 3 + 2] = -2 - ((index * 29) % 100) / 10;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  scene.add(
    new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        color: 0xbda8ff,
        size: 0.045,
        transparent: true,
        opacity: 0.85,
        blending: THREE.AdditiveBlending,
      })
    )
  );
};

const addNumberFace = (group, position, rotation, texture, color) => {
  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(0.88, 0.88),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  );
  label.position.copy(position);
  label.rotation.set(rotation.x, rotation.y, rotation.z);
  group.add(label);

  const glow = new THREE.Mesh(
    new THREE.PlaneGeometry(0.72, 0.72),
    new THREE.MeshBasicMaterial({
      map: texture,
      color,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    })
  );
  glow.position.copy(position);
  glow.rotation.set(rotation.x, rotation.y, rotation.z);
  group.add(glow);
};

const makeProjectile = (number, skin, dieType = null) => {
  const group = new THREE.Group();
  const geometry = new THREE.BoxGeometry(0.92, 0.92, 0.92);
  const material = new THREE.MeshPhysicalMaterial({
    color: skin.body,
    emissive: skin.emissive,
    emissiveIntensity: 1.1,
    metalness: skin.metalness ?? 0.78,
    roughness: skin.roughness ?? 0.18,
    clearcoat: 1,
  });
  const cube = new THREE.Mesh(geometry, material);
  group.userData.bodyMaterial = material;
  cube.castShadow = true;
  group.add(cube);
  group.add(
    new THREE.LineSegments(
      new THREE.EdgesGeometry(geometry),
      new THREE.LineBasicMaterial({
        color: skin.color,
        transparent: true,
        opacity: 0.9,
      })
    )
  );

  const texture = makeNumberTexture(formatRoll(number), skin.color, dieType);
  if (texture) {
    const half = 0.48;
    [
      [new THREE.Vector3(0, 0, half), new THREE.Euler(0, 0, 0)],
      [new THREE.Vector3(0, 0, -half), new THREE.Euler(0, Math.PI, 0)],
      [new THREE.Vector3(0, half, 0), new THREE.Euler(-Math.PI / 2, 0, 0)],
      [new THREE.Vector3(0, -half, 0), new THREE.Euler(Math.PI / 2, 0, 0)],
      [new THREE.Vector3(half, 0, 0), new THREE.Euler(0, Math.PI / 2, 0)],
      [new THREE.Vector3(-half, 0, 0), new THREE.Euler(0, -Math.PI / 2, 0)],
    ].forEach(([position, rotation]) =>
      addNumberFace(group, position, rotation, texture, skin.color)
    );
  }
  group.userData.numberTexture = texture;
  group.userData.numberColor = skin.color;
  group.userData.dieType = dieType;
  return group;
};

const GlitchCoreScene = ({
  forceFallback = false,
  stage = 'waiting',
  corePhase = 'containment',
  showPhaseBadge = true,
  intentName = 'Static barrage',
  charge = 0,
  attackStyle = null,
  playerRoll = null,
  dieType,
  bossRoll = null,
  bossHp = BOSS_MAX_HP,
  lethalHit = false,
  attackDamage = 0,
  criticalHit = false,
  playerAttackDamageBeforeShield = 0,
  playerAttackShieldBlocked = 0,
  coreAttackDamage = 0,
  coreAttackOutcome = 'hit',
  coreAttackType = null,
  onPlayerCoreContact,
  onCoreSpitComplete,
  onCoreImpactComplete,
  onWebglAvailabilityChange,
  stance = 'strike',
  enraged = false,
  relics = [],
  mirrorChipUsed = false,
  reduceMotion = false,
  className = '',
  decorative = false,
  children,
}) => {
  const mountRef = useRef(null);
  const propsRef = useRef({
    stage,
    corePhase,
    intentName,
    charge,
    attackStyle,
    playerRoll,
    dieType,
    bossRoll,
    bossHp,
    lethalHit,
    attackDamage,
    criticalHit,
    playerAttackDamageBeforeShield,
    playerAttackShieldBlocked,
    coreAttackDamage,
    coreAttackOutcome,
    coreAttackType,
    onPlayerCoreContact,
    onCoreSpitComplete,
    onCoreImpactComplete,
    stance,
    enraged,
    relics,
    mirrorChipUsed,
    reduceMotion,
    decorative,
  });
  const [webglAvailable, setWebglAvailable] = useState(!forceFallback);
  const [playerHit, setPlayerHit] = useState(null);
  const [incomingHit, setIncomingHit] = useState(null);
  const nextHitId = useRef(0);
  const contactReported = useRef(false);
  const awaitingPlayerContact = useRef(stage === 'playerRolling');
  const notifyPlayerContact = useCallback(() => {
    if (contactReported.current) return;
    contactReported.current = true;
    awaitingPlayerContact.current = false;
    const current = propsRef.current;
    setPlayerHit({
      id: ++nextHitId.current,
      damage: current.attackDamage,
      critical: current.criticalHit,
      blocked: current.playerAttackShieldBlocked,
    });
    current.onPlayerCoreContact?.();
  }, []);
  useEffect(() => {
    if (['waiting', 'ready', 'playerRolling'].includes(stage)) {
      contactReported.current = false;
      awaitingPlayerContact.current = stage === 'playerRolling';
      setPlayerHit(null);
    }
  }, [stage]);
  useEffect(() => {
    // Reduced motion or the combat failsafe can commit the hit before the
    // arena's next frame. Report that impact once, using the same hit feedback.
    if (
      awaitingPlayerContact.current &&
      ['relics', 'playerDone', 'playerOutcome'].includes(stage)
    )
      notifyPlayerContact();
  }, [stage, notifyPlayerContact]);
  const previousPhaseRef = useRef(corePhase);
  const [phaseAnnouncement, setPhaseAnnouncement] = useState(null);

  useEffect(() => {
    const previous = previousPhaseRef.current;
    previousPhaseRef.current = corePhase;
    if (previous === corePhase || corePhase === 'containment') return undefined;
    if (
      propsRef.current.bossHp <= 0 ||
      propsRef.current.stage === 'coreCollapse'
    )
      return undefined;
    setPhaseAnnouncement(corePhase);
    const timer = setTimeout(() => setPhaseAnnouncement(null), 2800);
    return () => clearTimeout(timer);
  }, [corePhase]);

  useEffect(() => {
    propsRef.current = {
      stage,
      corePhase,
      intentName,
      charge,
      attackStyle,
      playerRoll,
      dieType,
      bossRoll,
      bossHp,
      lethalHit,
      attackDamage,
      criticalHit,
      playerAttackDamageBeforeShield,
      playerAttackShieldBlocked,
      coreAttackDamage,
      coreAttackOutcome,
      coreAttackType,
      onPlayerCoreContact,
      onCoreSpitComplete,
      onCoreImpactComplete,
      stance,
      enraged,
      relics,
      mirrorChipUsed,
      reduceMotion,
      decorative,
    };
  }, [
    stage,
    corePhase,
    intentName,
    charge,
    attackStyle,
    playerRoll,
    dieType,
    bossRoll,
    bossHp,
    lethalHit,
    attackDamage,
    criticalHit,
    playerAttackDamageBeforeShield,
    playerAttackShieldBlocked,
    coreAttackDamage,
    coreAttackOutcome,
    coreAttackType,
    onPlayerCoreContact,
    onCoreSpitComplete,
    onCoreImpactComplete,
    stance,
    enraged,
    relics,
    mirrorChipUsed,
    reduceMotion,
    decorative,
  ]);

  useEffect(() => {
    onWebglAvailabilityChange?.(webglAvailable);
  }, [webglAvailable, onWebglAvailabilityChange]);

  useEffect(() => {
    const current = propsRef.current;
    if (
      stage === 'bossImpact' &&
      current.coreAttackDamage > 0 &&
      ['hit', 'protocol'].includes(current.coreAttackOutcome)
    ) {
      setIncomingHit({
        id: ++nextHitId.current,
        damage: current.coreAttackDamage,
      });
    } else if (
      ['waiting', 'ready', 'playerRolling', 'bossCharge'].includes(stage)
    ) {
      setIncomingHit(null);
    }
  }, [stage]);

  useEffect(() => {
    if (webglAvailable || stage !== 'playerRolling') return undefined;
    const timer = setTimeout(
      () => {
        notifyPlayerContact();
        propsRef.current.onCoreImpactComplete?.();
      },
      reduceMotion ? 0 : 650
    );
    return () => clearTimeout(timer);
  }, [webglAvailable, stage, reduceMotion, notifyPlayerContact]);

  useEffect(() => {
    if (webglAvailable || stage !== 'enemySpit') return undefined;
    const timer = setTimeout(
      () => propsRef.current.onCoreSpitComplete?.(),
      reduceMotion ? 0 : process.env.NODE_ENV === 'test' ? 500 : 1350
    );
    return () => clearTimeout(timer);
  }, [webglAvailable, stage, reduceMotion]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    const initialProps = propsRef.current;
    const isTestRenderer =
      typeof navigator !== 'undefined' &&
      /jsdom|node\.js/i.test(navigator.userAgent);
    if (
      forceFallback ||
      isTestRenderer ||
      (typeof window !== 'undefined' && !window.WebGLRenderingContext)
    ) {
      setWebglAvailable(false);
      return undefined;
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x030511);
    scene.fog = new THREE.FogExp2(0x050719, 0.045);
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(0, 4.3, 8.6);

    let renderer;
    try {
      renderer = createCoreboundRenderer({
        antialias: true,
        alpha: true,
      });
    } catch {
      setWebglAvailable(false);
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xaaa2ff, 0x05040e, 1.5));
    const keyLight = new THREE.DirectionalLight(0xffd9f5, 4.6);
    keyLight.position.set(-4, 7, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(1024, 1024);
    scene.add(keyLight);
    const cyanLight = new THREE.PointLight(0x59f4ff, 4.8, 10, 2);
    cyanLight.position.set(3, 2, 3);
    scene.add(cyanLight);
    const coreLight = new THREE.PointLight(0xd568ff, 6, 9, 1.8);
    coreLight.position.set(0, 1.1, 0);
    scene.add(coreLight);
    addStars(scene);
    const {
      core,
      coreMesh,
      coreMaterial,
      fracture,
      heart,
      rings,
      shards,
      shardMaterial,
    } = createCoreModel(scene, { hp: initialProps.bossHp });
    const destruction = createCoreDestruction(coreMesh, scene);
    const phaseColors = Object.fromEntries(
      Object.entries(CORE_APPEARANCE).map(([phase, appearance]) => [
        phase,
        {
          color: new THREE.Color(appearance.color),
          emissive: new THREE.Color(appearance.emissive),
          light: new THREE.Color(appearance.light),
        },
      ])
    );
    const atmosphere = {
      containment: new THREE.Color(0x030511),
      fracture: new THREE.Color(0x03141b),
      emergence: new THREE.Color(0x16091d),
      evolution: new THREE.Color(0x160511),
      meltdown: new THREE.Color(0x200704),
    };
    const phaseColor = new THREE.Color();
    const phaseEmissive = new THREE.Color();
    const phaseLightColor = new THREE.Color();
    const ringPhaseColor = new THREE.Color();
    const intentTellColor = new THREE.Color();
    const coreWireColor = new THREE.Color();
    const whiteHighlight = new THREE.Color(0xffffff);
    const coreBreakerWireColor = new THREE.Color(0xffb45e);
    const coreWire = fracture.wire;
    const initialPlayerDie = getDie(initialProps.dieType);
    const playerStyleAura = createAttackStyleAuraScene(scene);
    const playerProjectile = makeProjectile(
      initialProps.playerRoll,
      initialPlayerDie,
      initialPlayerDie.id
    );
    const initialCoreDie = getCoreDieAppearance(initialProps.corePhase);
    const enemyProjectile = makeProjectile(
      initialProps.bossRoll,
      initialCoreDie,
      initialCoreDie.dieType
    );
    const explosionColors = [0xffe58b, 0x80f7ff, 0xff72ce, 0xc69bff];
    const explosionShards = Array.from({ length: 32 }, (_, index) => {
      const material = new THREE.MeshBasicMaterial({
        color: explosionColors[index % explosionColors.length],
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.08 + (index % 4) * 0.035, 0),
        material
      );
      mesh.userData.velocity = new THREE.Vector3();
      mesh.visible = false;
      scene.add(mesh);
      const angle = (index / 32) * Math.PI * 2;
      const elevation = ((index * 13) % 17) / 16 - 0.5;
      const direction = new THREE.Vector3(
        Math.cos(angle),
        elevation,
        Math.sin(angle)
      ).normalize();
      return {
        mesh,
        direction,
        speed: 2.5 + ((index * 7) % 12) / 4,
        spin: new THREE.Vector3(
          1 + (index % 5),
          1.5 + (index % 7),
          0.8 + (index % 3)
        ),
      };
    });
    const explosionWave = new THREE.Mesh(
      new THREE.TorusGeometry(1, 0.065, 10, 64),
      new THREE.MeshBasicMaterial({
        color: 0xffe6a0,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    explosionWave.rotation.x = Math.PI / 2;
    explosionWave.visible = false;
    scene.add(explosionWave);
    const mirrorProjectile = playerProjectile.clone();
    mirrorProjectile.userData.numberTexture =
      playerProjectile.userData.numberTexture;
    mirrorProjectile.userData.numberColor =
      playerProjectile.userData.numberColor;
    scene.add(playerProjectile, mirrorProjectile, enemyProjectile);
    const enemyDieEffects = createCoreDieScene(scene, enemyProjectile, 0.92);
    playerProjectile.visible = false;
    mirrorProjectile.visible = false;
    enemyProjectile.visible = false;

    const beamGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(),
      new THREE.Vector3(),
    ]);
    const playerBeam = new THREE.Line(
      beamGeometry,
      new THREE.LineBasicMaterial({
        color: 0x65fff1,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
      })
    );
    const enemyBeam = new THREE.Line(
      beamGeometry.clone(),
      new THREE.LineBasicMaterial({
        color: 0xff5f9f,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
      })
    );
    scene.add(playerBeam, enemyBeam);
    const laser = createCoreLaser(scene);

    const coreHitPoint = new THREE.Vector3();
    const physics = {
      lastStage: null,
      corePhase: initialProps.corePhase,
      previousCorePhase: initialProps.corePhase,
      phaseTransitionElapsed: -1,
      elapsed: 0,
      flash: 0,
      cameraShake: 0,
      playerStart: new THREE.Vector3(-2.55, -0.35, 2.2),
      enemyStart: new THREE.Vector3(2.55, -0.35, 2.2),
      target: new THREE.Vector3(0, 0.8, 0),
      playerContactPosition: new THREE.Vector3(-0.94, 1.1, 0.92),
      playerImpactDirection: new THREE.Vector3(-0.94, 0.3, 0.92).normalize(),
      cameraTarget: new THREE.Vector3(0, 0.8, 0),
      cameraGoal: new THREE.Vector3(0, 4.3, 8.6),
      playerPhase: 'idle',
      playerStrikeElapsed: 0,
      playerStrikeId: 0,
      playerStrike: null,
      enemyPhase: 'idle',
      playerLandingElapsed: 0,
      enemyLandingElapsed: 0,
      playerCloseupElapsed: 0,
      enemyCloseupElapsed: 0,
      playerBounceElapsed: 0,
      playerBounceVelocity: new THREE.Vector3(),
      coreImpactActive: false,
      coreElapsed: 0,
      coreImpactComplete: false,
      enemySpitComplete: false,
      explosionElapsed: 0,
      explosionStarted: false,
      coreFlash: 0,
      playerLandingPosition: new THREE.Vector3(-1.25, -0.61, 1.05),
      enemyLandingPosition: new THREE.Vector3(1.25, -0.61, 1.05),
      playerLandingQuaternion: new THREE.Quaternion(),
      enemyLandingQuaternion: new THREE.Quaternion(),
    };
    const playerPosition = new THREE.Vector3();
    const playerStrike = createGuardianStrikeScene(scene, {
      target: physics.playerContactPosition,
      scale: 0.6,
      name: 'core-player-strike',
    });
    const enemyPosition = new THREE.Vector3();
    const enemySpinAxis = new THREE.Vector3()
      .crossVectors(
        physics.enemyStart.clone().setY(0).normalize(),
        new THREE.Vector3(0, 1, 0)
      )
      .normalize();
    const cameraFollowTarget = new THREE.Vector3();
    const cameraLook = new THREE.Vector3();
    const flatQuaternion = new THREE.Quaternion();
    let lastPlayerRoll = null;
    let lastDieType = null;
    let lastBossRoll = null;
    let lastCoreDieType = null;
    let frame;
    const clock = new THREE.Clock();

    const setBeam = (line, from, to, opacity) => {
      const attribute = line.geometry.getAttribute('position');
      attribute.setXYZ(0, from.x, from.y, from.z);
      attribute.setXYZ(1, to.x, to.y, to.z);
      attribute.needsUpdate = true;
      line.material.opacity = opacity;
    };

    const resetStage = (current, previousStage) => {
      physics.elapsed = 0;
      physics.flash = 0;
      physics.cameraShake = 0;
      explosionShards.forEach(({ mesh }) => {
        mesh.visible = false;
        mesh.material.opacity = 0;
      });
      explosionWave.visible = false;
      explosionWave.material.opacity = 0;
      coreMaterial.opacity = 0.95;
      coreWire.material.opacity = 0.72;
      playerProjectile.visible = false;
      mirrorProjectile.visible = false;
      enemyProjectile.visible = false;
      playerBeam.material.opacity = 0;
      enemyBeam.material.opacity = 0;

      if (current.stage === 'ready' && !current.decorative) {
        const frameWidth = getCoreAppearance(current.corePhase).frameWidth;
        const distance = Math.max(5.2, frameWidth / camera.aspect);
        physics.cameraTarget.copy(CORE_HOME);
        physics.cameraGoal.set(0, CORE_HOME.y + distance * 0.2, distance);
        camera.position.copy(physics.cameraGoal);
        cameraLook.copy(CORE_HOME);
        camera.lookAt(CORE_HOME);
      } else if (current.stage === 'playerRolling') {
        physics.playerStrikeElapsed = 0;
        physics.playerStrikeId += 1;
        physics.playerStrike = {
          combatId: `core:${physics.playerStrikeId}`,
          effectiveRoll: current.attackDamage,
          launchAt: current.reduceMotion ? 0 : 350,
          hitAt: (current.reduceMotion ? 0 : 350) + 900,
        };
        physics.playerPhase = 'flight';
        physics.playerLandingElapsed = 0;
        physics.playerBounceElapsed = 0;
        physics.playerBounceVelocity.set(0, 0, 0);
        physics.coreImpactActive = false;
        physics.coreElapsed = 0;
        physics.coreImpactComplete = false;
        physics.coreFlash = 0;
        core.position.copy(CORE_HOME);
        playerProjectile.position.copy(physics.playerStart);
        playerProjectile.rotation.set(0, 0, 0);
        playerProjectile.visible = true;
      } else if (current.stage === 'enemySpit') {
        core.position.copy(CORE_HOME);
        physics.enemyPhase = 'spitting';
        physics.enemySpitComplete = false;
        updateNumberTexture(
          enemyProjectile.userData.numberTexture,
          '?',
          enemyProjectile.userData.numberColor,
          enemyProjectile.userData.dieType
        );
        enemyProjectile.position.copy(CORE_HOME);
        enemyProjectile.rotation.set(0, 0, 0);
        enemyProjectile.visible = true;
        physics.flash = 1.1;
      } else if (['relics', 'playerDone'].includes(current.stage)) {
        if (
          previousStage === 'playerRolling' ||
          physics.playerPhase === 'idle'
        ) {
          physics.playerPhase = 'impact';
          physics.playerLandingElapsed = 0;
          physics.playerCloseupElapsed = 0;
          physics.playerLandingQuaternion.copy(playerProjectile.quaternion);
        }
        if (physics.playerPhase !== 'landing') {
          playerProjectile.position.copy(physics.target);
        }
        playerProjectile.visible = true;
        if (current.mirrorChipUsed) {
          mirrorProjectile.position.set(0.18, 0.88, 0.1);
          mirrorProjectile.visible = true;
        }
        physics.flash = Math.min(1.6, 0.85 + current.attackDamage / 20);
      } else if (current.stage === 'enemyRolling') {
        core.position.copy(CORE_HOME);
        physics.coreImpactActive = false;
        physics.enemyPhase = 'flight';
        physics.enemyLandingElapsed = 0;
        enemyProjectile.position.copy(physics.enemyStart);
        enemyProjectile.rotation.set(0, 0, 0);
        enemyProjectile.visible = true;
      } else if (current.stage === 'enemyDone') {
        if (previousStage === 'enemyRolling' || physics.enemyPhase === 'idle') {
          physics.enemyPhase = 'landing';
          physics.enemyLandingElapsed = 0;
          physics.enemyCloseupElapsed = 0;
          physics.enemyLandingQuaternion.copy(enemyProjectile.quaternion);
        }
        enemyProjectile.position.copy(physics.enemyStart);
        enemyProjectile.visible = true;
        physics.cameraShake = 0.12;
      } else if (
        ['bossCharge', 'bossFire', 'bossImpact', 'bossOutcome'].includes(
          current.stage
        )
      ) {
        core.position.copy(CORE_HOME);
        physics.coreImpactActive = false;
        physics.cameraTarget.copy(CORE_HOME);
        cameraLook.copy(CORE_HOME);
        const frameWidth =
          getCoreAppearance(current.corePhase).frameWidth - 0.3;
        const distance = Math.max(6.4, frameWidth / camera.aspect);
        physics.cameraGoal.set(0, CORE_HOME.y + distance * 0.2, distance);
        if (current.stage === 'bossCharge') {
          camera.position.copy(physics.cameraGoal);
          camera.lookAt(CORE_HOME);
        }
      } else if (current.stage === 'coreCollapse') {
        physics.explosionElapsed = 0;
        physics.explosionStarted = false;
        core.scale.setScalar(1);
        fracture.shatterAll(
          physics.playerContactPosition,
          current.reduceMotion
        );
        destruction.start(heart.group.visible ? heart.body : coreMesh);
        playerProjectile.visible =
          previousStage === 'playerRolling' &&
          physics.coreImpactActive &&
          physics.playerBounceElapsed < 1.1;
        playerBeam.material.opacity = 0;
        physics.flash = 1.6;
        physics.cameraShake = 0.18;
      }
    };

    const animate = () => {
      frame = requestAnimationFrame(animate);
      const frameDelta = clock.getDelta();
      const delta = Math.min(0.045, frameDelta);
      const elapsed = clock.elapsedTime;
      const current = propsRef.current;
      const stageName = current.stage;
      const chargeFactor = THREE.MathUtils.clamp(current.charge, 0, 1);

      if (physics.lastStage !== stageName) {
        const previousStage = physics.lastStage;
        physics.lastStage = stageName;
        resetStage(current, previousStage);
      }
      if (physics.corePhase !== current.corePhase) {
        physics.previousCorePhase = physics.corePhase;
        physics.corePhase = current.corePhase;
        physics.phaseTransitionElapsed = -1;
        if (current.bossHp > 0 && stageName !== 'coreCollapse') {
          physics.phaseTransitionElapsed = 0;
          physics.flash = Math.max(physics.flash, 1.35);
          physics.cameraShake = current.reduceMotion ? 0 : 0.12;
        }
      }
      const playerDie = getDie(current.dieType);
      const dieChanged = lastDieType !== playerDie.id;
      if (dieChanged) {
        lastDieType = playerDie.id;
        const material = playerProjectile.userData.bodyMaterial;
        material.color.set(playerDie.body);
        material.emissive.set(playerDie.emissive);
        material.metalness = playerDie.metalness;
        material.roughness = playerDie.roughness;
        playerProjectile.userData.numberColor = playerDie.color;
        playerProjectile.userData.dieType = playerDie.id;
        playerProjectile.children.forEach((child) => {
          if (
            child.isLineSegments ||
            child.material?.blending === THREE.AdditiveBlending
          )
            child.material.color.set(playerDie.color);
        });
        playerBeam.material.color.set(playerDie.color);
      }
      if (lastPlayerRoll !== current.playerRoll || dieChanged) {
        updateNumberTexture(
          playerProjectile.userData.numberTexture,
          formatRoll(current.playerRoll),
          playerProjectile.userData.numberColor,
          playerProjectile.userData.dieType
        );
        lastPlayerRoll = current.playerRoll;
      }
      const coreDie = getCoreDieAppearance(current.corePhase);
      const coreDieChanged = lastCoreDieType !== coreDie.dieType;
      if (coreDieChanged) {
        lastCoreDieType = coreDie.dieType;
        const material = enemyProjectile.userData.bodyMaterial;
        material.color.set(coreDie.body);
        material.emissive.set(coreDie.emissive);
        material.metalness = coreDie.metalness;
        material.roughness = coreDie.roughness;
        enemyProjectile.userData.numberColor = coreDie.color;
        enemyProjectile.userData.dieType = coreDie.dieType;
        enemyProjectile.children.forEach((child) => {
          if (child.isLineSegments) child.material.color.set(coreDie.accent);
          else if (child.material?.blending === THREE.AdditiveBlending)
            child.material.color.set(coreDie.color);
        });
        enemyBeam.material.color.set(coreDie.accent);
      }
      if (lastBossRoll !== current.bossRoll || coreDieChanged) {
        updateNumberTexture(
          enemyProjectile.userData.numberTexture,
          stageName === 'enemySpit' ? '?' : formatRoll(current.bossRoll),
          enemyProjectile.userData.numberColor,
          enemyProjectile.userData.dieType
        );
        lastBossRoll = current.bossRoll;
      }

      const hpRatio = THREE.MathUtils.clamp(current.bossHp / BOSS_MAX_HP, 0, 1);
      if (!['playerRolling', 'coreCollapse'].includes(stageName)) {
        fracture.syncHealth(current.bossHp);
      }
      destruction.group.visible = stageName === 'coreCollapse';
      shardMaterial.opacity = 0.8;
      const phaseLevelTarget = CORE_PHASE_LEVEL[current.corePhase] || 0;
      const transitionActive = physics.phaseTransitionElapsed >= 0;
      const transitionProgress = transitionActive
        ? current.reduceMotion
          ? 1
          : THREE.MathUtils.clamp(
              physics.phaseTransitionElapsed / CORE_PHASE_TRANSITION_SECONDS,
              0,
              1
            )
        : 1;
      const easedPhaseTransition =
        transitionProgress * transitionProgress * (3 - 2 * transitionProgress);
      const previousPhaseLevel =
        CORE_PHASE_LEVEL[physics.previousCorePhase] || 0;
      const phaseLevel = transitionActive
        ? THREE.MathUtils.lerp(
            previousPhaseLevel,
            phaseLevelTarget,
            easedPhaseTransition
          )
        : phaseLevelTarget;
      const {
        fracture: fractureBlend,
        emergence: emergenceBlend,
        evolution: evolutionBlend,
        meltdown: meltdownBlend,
        heart: heartBlend,
        shellOpenness,
      } = getCoreVisualState(phaseLevel);
      fracture.setOpenness(shellOpenness);
      const phaseTransitionPulse = transitionActive
        ? Math.sin(Math.PI * transitionProgress)
        : 0;
      if (transitionActive) {
        if (
          current.reduceMotion ||
          physics.phaseTransitionElapsed >= CORE_PHASE_TRANSITION_SECONDS
        ) {
          physics.phaseTransitionElapsed = -1;
        } else {
          physics.phaseTransitionElapsed += frameDelta;
        }
      }
      const lowerPhaseIndex = Math.min(
        CORE_PHASE_ORDER.length - 1,
        Math.floor(phaseLevel)
      );
      const upperPhaseIndex = Math.min(
        CORE_PHASE_ORDER.length - 1,
        lowerPhaseIndex + 1
      );
      const phaseColorBlend = phaseLevel - lowerPhaseIndex;
      const lowerPhaseColors = phaseColors[CORE_PHASE_ORDER[lowerPhaseIndex]];
      const upperPhaseColors = phaseColors[CORE_PHASE_ORDER[upperPhaseIndex]];
      phaseColor
        .copy(lowerPhaseColors.color)
        .lerp(upperPhaseColors.color, phaseColorBlend);
      phaseEmissive
        .copy(lowerPhaseColors.emissive)
        .lerp(upperPhaseColors.emissive, phaseColorBlend);
      phaseLightColor
        .copy(lowerPhaseColors.light)
        .lerp(upperPhaseColors.light, phaseColorBlend);
      const heat = meltdownBlend > 0 ? 1 : 1 - hpRatio;
      const hasCoreBreaker = current.relics.includes('coreBreaker');
      const hasFirewall = current.relics.includes('firewall');
      const hasLuckySeven = current.relics.includes('luckySeven');
      const mirrorChipActivated = current.mirrorChipUsed;
      if (stageName !== 'coreCollapse') core.scale.setScalar(1);
      coreMaterial.opacity =
        0.95 -
        fractureBlend * 0.08 -
        emergenceBlend * 0.12 -
        evolutionBlend * 0.12 -
        meltdownBlend * 0.22;
      coreMaterial.color.copy(phaseColor);
      coreMaterial.emissive.copy(phaseEmissive);
      coreMaterial.emissiveIntensity =
        1.3 +
        heat * 2.8 +
        (hasCoreBreaker ? 1.2 : 0) +
        physics.coreFlash * 5 +
        phaseTransitionPulse * 2.2;
      coreWireColor.copy(phaseColor).lerp(whiteHighlight, 0.48);
      coreWire.material.color.copy(
        hasCoreBreaker ? coreBreakerWireColor : coreWireColor
      );
      coreLight.color.copy(phaseLightColor);
      coreLight.intensity =
        5 +
        heat * 5 +
        physics.flash * 8 +
        (hasLuckySeven ? 1.5 : 0) +
        phaseTransitionPulse * 7;
      cyanLight.intensity = 3.5 + chargeFactor * 4;
      scene.background
        .copy(atmosphere[CORE_PHASE_ORDER[lowerPhaseIndex]])
        .lerp(atmosphere[CORE_PHASE_ORDER[upperPhaseIndex]], phaseColorBlend);
      scene.fog.color.copy(scene.background);

      if (heartBlend > 0.01) {
        const yaw = Math.atan2(
          Math.sin(core.rotation.y),
          Math.cos(core.rotation.y)
        );
        core.rotation.y = THREE.MathUtils.lerp(
          yaw,
          current.reduceMotion ? 0 : Math.sin(elapsed * 0.6) * 0.12,
          current.reduceMotion ? 1 : Math.min(1, delta * 4) * heartBlend
        );
      } else if (!current.reduceMotion) {
        core.rotation.y += delta * (0.3 + heat * 0.8);
      }
      core.rotation.x = current.reduceMotion
        ? 0
        : Math.sin(elapsed * 0.7) * 0.08;
      core.rotation.z = current.reduceMotion
        ? 0
        : Math.sin(elapsed * 1.3) * evolutionBlend * 0.035;
      if (!['playerRolling', 'coreCollapse'].includes(stageName)) {
        core.position.copy(CORE_HOME);
        if (!current.reduceMotion)
          core.position.y += Math.sin(elapsed * 1.6) * evolutionBlend * 0.055;
      }
      const evolvedTellActive =
        heartBlend > 0.3 && ['bossWarning', 'bossCharge'].includes(stageName);
      const tellColor = CORE_INTENT_TELL_COLORS[current.intentName];
      if (evolvedTellActive && tellColor) intentTellColor.setHex(tellColor);
      const tellPulse = evolvedTellActive
        ? 0.5 + 0.5 * Math.sin(elapsed * 7)
        : 0;
      const attackSpin = !evolvedTellActive
        ? 1
        : current.intentName === 'Siphon pulse'
        ? -1.45
        : current.intentName === 'Worldbreaker'
        ? 0.62
        : 1.85;
      rings.forEach((ring, index) => {
        ring.rotation.z +=
          delta * ring.userData.speed * (1 + heat * 0.9) * attackSpin;
        ring.rotation.x +=
          delta *
          ((index % 2 ? -0.17 : 0.11) +
            evolutionBlend * 0.18 +
            meltdownBlend * 0.25);
        const ringTellMix = evolvedTellActive ? 0.78 : 0;
        ringPhaseColor
          .copy(ring.userData.baseColor)
          .lerp(
            phaseColor,
            fractureBlend * 0.3 + heartBlend * 0.35 + meltdownBlend * 0.35
          );
        if (evolvedTellActive && tellColor)
          ringPhaseColor.lerp(intentTellColor, ringTellMix);
        if (index === 2 && hasFirewall && !evolvedTellActive)
          ringPhaseColor.set(0x65b8ff);
        ring.material.color.copy(ringPhaseColor);
        ring.material.opacity = THREE.MathUtils.clamp(
          (index === 0 ? 0.68 : 0.36) +
            heat * 0.14 +
            phaseTransitionPulse * 0.25 +
            (evolvedTellActive && current.intentName === 'Static barrage'
              ? tellPulse * 0.24
              : 0),
          0,
          1
        );
        const ringScale =
          evolvedTellActive && current.intentName === 'Worldbreaker'
            ? 1 - tellPulse * 0.16
            : evolvedTellActive && current.intentName === 'Siphon pulse'
            ? 0.9 + tellPulse * 0.12
            : 1 +
              (evolvedTellActive && current.intentName === 'Static barrage'
                ? tellPulse * 0.07
                : 0);
        ring.scale.setScalar(
          ringScale * (1 + fractureBlend * 0.08 + emergenceBlend * 0.06)
        );
      });
      shardMaterial.color.copy(phaseColor).lerp(whiteHighlight, 0.25);
      shards.forEach((shard, index) => {
        shard.position.lerpVectors(
          shard.userData.basePosition,
          shard.userData.fracturePosition,
          fractureBlend
        );
        shard.position.lerp(
          shard.userData.crownPosition,
          emergenceBlend * 0.35 + evolutionBlend * 0.65
        );
        if (!current.reduceMotion)
          shard.position.y +=
            Math.sin(elapsed * 2 + index) *
            fractureBlend *
            (1 - evolutionBlend) *
            0.12;
        if (meltdownBlend > 0) {
          shard.position.lerpVectors(
            shard.userData.crownPosition,
            shard.userData.meltdownPosition,
            meltdownBlend
          );
        }
        shard.rotation.x += delta * (0.4 + index * 0.03);
        shard.rotation.y -=
          delta *
          (0.55 + heat * 0.4 + evolutionBlend * 0.55 + meltdownBlend * 1.1);
      });
      physics.coreFlash = Math.max(0, physics.coreFlash - delta * 2.4);

      if (stageName === 'playerRolling') {
        physics.elapsed += delta;
        const progress = THREE.MathUtils.clamp(
          (physics.elapsed - physics.playerStrike.launchAt / 1000) / 0.9,
          0,
          1
        );
        playerPosition.lerpVectors(
          physics.playerStart,
          physics.playerContactPosition,
          progress
        );
        const stanceLift =
          current.stance === 'overload'
            ? 1.3
            : current.stance === 'guard'
            ? 0.82
            : 1;
        playerPosition.y +=
          Math.sin(progress * Math.PI) *
          (0.85 + chargeFactor * 0.95) *
          stanceLift;
        if (!physics.coreImpactActive) {
          playerProjectile.position.copy(playerPosition);
        }
        if (progress < 1) {
          playerProjectile.rotateOnAxis(
            DIE_DRILL_AXIS,
            getDieDrillAngle(
              delta * 1000,
              chargeFactor,
              current.relics.includes('overclock')
            )
          );
        }
        if (mirrorChipActivated && progress < 1) {
          mirrorProjectile.visible = true;
          mirrorProjectile.position.set(
            playerPosition.x * 0.94,
            playerPosition.y + 0.18,
            playerPosition.z - 0.14
          );
          mirrorProjectile.rotation.copy(playerProjectile.rotation);
          mirrorProjectile.rotation.y *= -1;
        } else if (progress >= 1) {
          mirrorProjectile.visible = false;
        }
        setBeam(
          playerBeam,
          playerProjectile.position,
          physics.target,
          progress > 0.4 ? 0.28 : 0
        );
        cameraFollowTarget.set(
          THREE.MathUtils.clamp(playerProjectile.position.x * 0.08, -0.3, 0.3),
          0.75 +
            THREE.MathUtils.clamp(
              playerProjectile.position.y * 0.06,
              -0.1,
              0.16
            ),
          THREE.MathUtils.clamp(playerProjectile.position.z * 0.04, -0.12, 0.12)
        );
        physics.cameraTarget.lerp(cameraFollowTarget, Math.min(1, delta * 4));
        physics.cameraGoal.set(
          THREE.MathUtils.clamp(
            playerProjectile.position.x * 0.06,
            -0.22,
            0.22
          ),
          4.3,
          8.6
        );
        if (progress >= 1 && !physics.coreImpactActive) {
          physics.coreImpactActive = true;
          physics.coreElapsed = 0;
          physics.coreImpactComplete = false;
          physics.flash = current.attackDamage > 0 ? 1.4 : 0.5;
          physics.coreFlash = current.attackDamage > 0 ? 1 : 0;
          physics.cameraShake = current.reduceMotion ? 0 : 0.22;
          playerProjectile.position.copy(physics.playerContactPosition);
          physics.playerBounceElapsed = 0;
          physics.playerBounceVelocity
            .copy(physics.playerImpactDirection)
            .multiplyScalar(3.1);
          physics.playerBounceVelocity.y += 1.7;
          if (current.lethalHit) {
            fracture.shatterAll(
              playerProjectile.position,
              current.reduceMotion
            );
          } else {
            fracture.breakOff(
              playerProjectile.position,
              current.attackDamage,
              current.reduceMotion
            );
          }
          notifyPlayerContact();
        }

        if (physics.coreImpactActive) {
          physics.coreElapsed += delta;
          const impactPulse = Math.max(0, 1 - physics.coreElapsed / 0.8);
          physics.flash = Math.max(
            0,
            (current.attackDamage > 0 ? 1.4 : 0.5) - physics.coreElapsed * 3.5
          );
          physics.cameraShake = current.reduceMotion
            ? 0
            : 0.22 * Math.exp(-physics.coreElapsed * 8);
          if (!current.reduceMotion && !current.lethalHit) {
            core.scale.setScalar(1 + impactPulse * 0.08);
            const recoil =
              Math.sin(Math.min(1, physics.coreElapsed / 0.42) * Math.PI) *
              0.16;
            core.position
              .copy(CORE_HOME)
              .addScaledVector(physics.playerImpactDirection, -recoil);
          }
          physics.cameraTarget.lerp(CORE_HOME, Math.min(1, delta * 10));
          const frameWidth = getCoreAppearance(current.corePhase).frameWidth;
          const impactDistance = Math.max(5.4, frameWidth / camera.aspect);
          physics.cameraGoal.set(
            -0.35,
            CORE_HOME.y + impactDistance * 0.35,
            impactDistance
          );
          setBeam(
            playerBeam,
            playerProjectile.position,
            core.position,
            Math.max(0, 0.32 - physics.playerBounceElapsed * 3)
          );
          if (!physics.coreImpactComplete && physics.coreElapsed >= 2.3) {
            physics.coreImpactComplete = true;
            current.onCoreImpactComplete?.();
          }
        }
      } else if (
        ['bossCharge', 'bossFire', 'bossImpact', 'bossOutcome'].includes(
          stageName
        )
      ) {
        physics.elapsed += delta;
        const progress = Math.min(
          1,
          physics.elapsed /
            (getCoreLaserDuration(
              stageName,
              current.reduceMotion,
              current.coreAttackType
            ) /
              1000)
        );
        const charging = stageName === 'bossCharge' ? progress : 0;
        core.scale.setScalar(current.reduceMotion ? 1 : 1 - charging * 0.12);
        if (!current.reduceMotion)
          core.position.z -= charging * evolutionBlend * 0.2;
        coreMaterial.emissiveIntensity +=
          charging * (current.coreAttackType === 'massiveAttack' ? 7 : 4);
        coreLight.intensity +=
          charging * (current.coreAttackType === 'massiveAttack' ? 14 : 8);
        if (stageName === 'bossFire') {
          core.scale.setScalar(
            current.reduceMotion ? 1 : 1 + Math.sin(progress * Math.PI) * 0.12
          );
          coreLight.intensity += (1 - progress) * 10;
          if (!current.reduceMotion)
            core.position.z +=
              Math.sin(progress * Math.PI) *
              evolutionBlend *
              (0.25 + meltdownBlend * 0.3);
        }
        const shakePower =
          current.coreAttackOutcome === 'hit'
            ? 0.14
            : ['block', 'protocol'].includes(current.coreAttackOutcome)
            ? 0.035
            : 0;
        physics.cameraShake =
          stageName === 'bossImpact' && !current.reduceMotion
            ? shakePower * Math.exp(-physics.elapsed * 9)
            : 0;
        const frameWidth =
          getCoreAppearance(current.corePhase).frameWidth - 0.3;
        const distance = Math.max(6.4, frameWidth / camera.aspect);
        physics.cameraGoal.set(0, CORE_HOME.y + distance * 0.2, distance);
        physics.cameraTarget.copy(CORE_HOME);
      } else if (stageName === 'enemySpit') {
        physics.elapsed += delta;
        const progress = THREE.MathUtils.clamp(physics.elapsed / 1.35, 0, 1);
        const eased = progress * progress * (3 - 2 * progress);
        enemyPosition.lerpVectors(CORE_HOME, physics.enemyStart, eased);
        enemyPosition.y += Math.sin(progress * Math.PI) * 0.45;
        enemyProjectile.position.copy(enemyPosition);
        enemyProjectile.rotateOnWorldAxis(enemySpinAxis, delta * 11);
        const spitPower = Math.sin(Math.min(1, progress / 0.28) * Math.PI);
        core.scale.setScalar(1 + spitPower * 0.22);
        setBeam(
          enemyBeam,
          core.position,
          enemyProjectile.position,
          0.15 + spitPower * 0.5
        );
        if (progress >= 1 && !physics.enemySpitComplete) {
          physics.enemySpitComplete = true;
          current.onCoreSpitComplete?.();
        }
        physics.cameraTarget.lerp(enemyPosition, Math.min(1, delta * 4));
        physics.cameraGoal.set(
          THREE.MathUtils.clamp(enemyProjectile.position.x * 0.06, -0.22, 0.22),
          4.3,
          8.6
        );
      } else if (stageName === 'enemyRolling') {
        physics.elapsed += delta;
        const progress = THREE.MathUtils.clamp(physics.elapsed / 1.7, 0, 1);
        enemyPosition.lerpVectors(physics.target, physics.enemyStart, progress);
        enemyPosition.y += Math.sin(progress * Math.PI) * 0.9;
        enemyProjectile.position.copy(enemyPosition);
        enemyProjectile.rotateOnWorldAxis(enemySpinAxis, delta * 11);
        setBeam(
          enemyBeam,
          physics.target,
          enemyProjectile.position,
          progress > 0.25 ? 0.34 : 0
        );
        cameraFollowTarget.set(
          THREE.MathUtils.clamp(enemyProjectile.position.x * 0.08, -0.3, 0.3),
          0.75 +
            THREE.MathUtils.clamp(
              enemyProjectile.position.y * 0.06,
              -0.1,
              0.16
            ),
          THREE.MathUtils.clamp(enemyProjectile.position.z * 0.04, -0.12, 0.12)
        );
        physics.cameraTarget.lerp(cameraFollowTarget, Math.min(1, delta * 4));
        physics.cameraGoal.set(
          THREE.MathUtils.clamp(enemyProjectile.position.x * 0.06, -0.22, 0.22),
          4.3,
          8.6
        );
      } else {
        physics.cameraTarget.lerp(physics.target, Math.min(1, delta * 3));
        if (stageName === 'ready' && !current.decorative) {
          const distance = Math.max(5.2, 4.6 / camera.aspect);
          physics.cameraGoal.set(0, CORE_HOME.y + distance * 0.2, distance);
        } else {
          physics.cameraGoal.set(0, 4.3, 8.6);
        }
        if (['relics', 'playerDone'].includes(stageName)) {
          physics.playerLandingElapsed += delta;
          const landingElapsed = physics.playerLandingElapsed;
          const impactDuration = 0.28;
          const landingDuration = 0.82;
          if (
            physics.playerPhase === 'impact' &&
            landingElapsed < impactDuration
          ) {
            playerProjectile.position.copy(physics.target);
            playerProjectile.rotateOnAxis(DIE_DRILL_AXIS, delta * 11);
            physics.flash = Math.max(0.8, 1.6 - landingElapsed * 2.2);
            physics.cameraShake = 0.18;
            core.scale.setScalar(
              1 + Math.sin(landingElapsed * 44) * 0.16 * physics.flash
            );
            setBeam(
              playerBeam,
              playerProjectile.position,
              physics.target,
              0.75
            );
          } else {
            if (physics.playerPhase === 'impact') {
              physics.playerPhase = 'landing';
              physics.playerLandingElapsed = 0;
              physics.playerLandingQuaternion.copy(playerProjectile.quaternion);
            }
            const landingProgress = THREE.MathUtils.clamp(
              physics.playerLandingElapsed / landingDuration,
              0,
              1
            );
            const easedLanding = 1 - Math.pow(1 - landingProgress, 2);
            playerPosition.lerpVectors(
              physics.target,
              physics.playerLandingPosition,
              easedLanding
            );
            playerPosition.y += Math.sin(landingProgress * Math.PI) * 0.3;
            playerProjectile.position.copy(playerPosition);
            playerProjectile.quaternion
              .copy(physics.playerLandingQuaternion)
              .slerp(flatQuaternion, easedLanding);
            if (landingProgress >= 1) {
              physics.playerPhase = 'closeup';
              physics.playerCloseupElapsed = 0;
            }
            physics.flash = Math.max(0, 0.75 - landingProgress * 0.75);
            physics.cameraShake = Math.max(0, 0.1 - landingProgress * 0.1);
            setBeam(
              playerBeam,
              playerProjectile.position,
              physics.target,
              physics.flash * 0.3
            );
          }
          if (physics.playerPhase === 'closeup') {
            playerProjectile.position.copy(physics.playerLandingPosition);
            playerProjectile.quaternion.copy(flatQuaternion);
            physics.playerCloseupElapsed += delta;
            if (physics.playerCloseupElapsed < 2) {
              physics.cameraTarget.lerp(
                physics.playerLandingPosition,
                Math.min(1, delta * 5)
              );
              physics.cameraGoal.set(
                physics.playerLandingPosition.x * 0.72,
                1.1,
                4.05
              );
            } else {
              physics.playerPhase = 'settled';
            }
          }
          physics.flash = Math.max(0, physics.flash - delta * 0.5);
          mirrorProjectile.rotation.y -= delta * 0.8;
        } else if (stageName === 'enemyDone') {
          physics.enemyLandingElapsed += delta;
          const landingProgress = THREE.MathUtils.clamp(
            physics.enemyLandingElapsed / 0.7,
            0,
            1
          );
          const easedLanding = 1 - Math.pow(1 - landingProgress, 2);
          enemyPosition.lerpVectors(
            physics.enemyStart,
            physics.enemyLandingPosition,
            easedLanding
          );
          enemyPosition.y += Math.sin(landingProgress * Math.PI) * 0.22;
          enemyProjectile.position.copy(enemyPosition);
          enemyProjectile.quaternion
            .copy(physics.enemyLandingQuaternion)
            .slerp(flatQuaternion, easedLanding);
          if (landingProgress >= 1) {
            physics.enemyPhase = 'closeup';
            physics.enemyCloseupElapsed = 0;
          }
          if (physics.enemyPhase === 'closeup') {
            enemyProjectile.position.copy(physics.enemyLandingPosition);
            enemyProjectile.quaternion.copy(flatQuaternion);
            physics.enemyCloseupElapsed += delta;
            if (physics.enemyCloseupElapsed < 2) {
              physics.cameraTarget.lerp(
                physics.enemyLandingPosition,
                Math.min(1, delta * 5)
              );
              physics.cameraGoal.set(
                physics.enemyLandingPosition.x * 0.72,
                1.1,
                4.05
              );
            } else {
              physics.enemyPhase = 'settled';
            }
          }
          physics.cameraShake = Math.max(0, physics.cameraShake - delta * 0.35);
        } else if (stageName === 'coreCollapse') {
          physics.elapsed += delta;
          physics.explosionElapsed += delta;
          destruction.update(physics.explosionElapsed, current.reduceMotion);
          const blastElapsed = Math.max(
            0,
            physics.explosionElapsed - CORE_SPLIT_DURATION
          );
          const collapseProgress = THREE.MathUtils.clamp(
            blastElapsed / CORE_EXPLOSION_DURATION,
            0,
            1
          );
          if (
            !physics.explosionStarted &&
            physics.explosionElapsed >= CORE_SPLIT_DURATION
          ) {
            physics.explosionStarted = true;
            explosionShards.forEach(({ mesh, direction, speed }, index) => {
              destruction.getShardOrigin(index, mesh.position);
              mesh.material.opacity = 1;
              mesh.visible = true;
              mesh.userData.velocity.copy(direction).multiplyScalar(speed);
              mesh.userData.velocity.x += index % 2 ? 1.1 : -1.1;
            });
            explosionWave.position.copy(destruction.group.position);
            explosionWave.scale.setScalar(0.18);
            explosionWave.material.opacity = 1;
            explosionWave.visible = true;
          }
          const burstOut = THREE.MathUtils.clamp(collapseProgress / 0.18, 0, 1);
          const collapseIn = THREE.MathUtils.clamp(
            (collapseProgress - 0.18) / 0.82,
            0,
            1
          );
          physics.flash = physics.explosionStarted
            ? Math.max(0, 1.8 - collapseProgress * 1.8)
            : Math.max(0, 1.4 - physics.explosionElapsed * 4);
          core.scale.setScalar(
            THREE.MathUtils.lerp(1, 1.38, burstOut) *
              Math.max(0.025, 1 - collapseIn * 0.98)
          );
          const orbitFade = Math.max(0, 1 - physics.explosionElapsed / 0.85);
          rings.forEach((ring) => {
            ring.material.opacity *= orbitFade;
          });
          shardMaterial.opacity *= orbitFade;
          explosionShards.forEach(({ mesh, spin }) => {
            mesh.position.addScaledVector(mesh.userData.velocity, delta);
            mesh.userData.velocity.y -= 1.15 * delta;
            mesh.rotation.x += spin.x * delta;
            mesh.rotation.y += spin.y * delta;
            mesh.rotation.z += spin.z * delta;
            mesh.material.opacity = Math.max(0, 1 - collapseProgress * 1.12);
          });
          explosionWave.scale.setScalar(0.18 + collapseProgress * 4.8);
          explosionWave.material.opacity = Math.max(
            0,
            1 - collapseProgress * 1.25
          );
          physics.cameraTarget.lerp(
            destruction.group.position,
            Math.min(1, delta * 8)
          );
          const distance = physics.explosionStarted
            ? Math.max(7.2, 5.4 / camera.aspect)
            : Math.max(
                5.4,
                (current.corePhase === 'containment' ? 3.8 : 5.2) /
                  camera.aspect
              );
          physics.cameraGoal.set(
            destruction.group.position.x,
            destruction.group.position.y + distance * 0.28,
            destruction.group.position.z + distance
          );
          physics.cameraShake = physics.explosionStarted
            ? 0.28 * Math.exp(-blastElapsed * 4)
            : 0.1 *
              Math.sin(
                Math.min(1, physics.explosionElapsed / CORE_SPLIT_DURATION) *
                  Math.PI
              );
        }
      }

      if (
        physics.coreImpactActive &&
        ['playerRolling', 'coreCollapse'].includes(stageName)
      ) {
        physics.playerBounceElapsed += delta;
        if (physics.playerBounceElapsed < 1.1 && playerProjectile.visible) {
          playerProjectile.position.addScaledVector(
            physics.playerBounceVelocity,
            delta
          );
          physics.playerBounceVelocity.y -= 5.6 * delta;
          playerProjectile.rotateOnWorldAxis(DIE_DRILL_AXIS, delta * 16);
        } else {
          playerProjectile.visible = false;
        }
      }
      fracture.update(delta, current.reduceMotion);
      if (
        evolutionBlend > 0.98 &&
        ['playerOutcome', 'bossWarning'].includes(stageName)
      ) {
        const distance = Math.max(
          6.2,
          getCoreAppearance(current.corePhase).frameWidth / camera.aspect
        );
        physics.cameraGoal.set(0, CORE_HOME.y + distance * 0.2, distance);
        physics.cameraTarget.copy(CORE_HOME);
      }
      if (transitionActive && stageName !== 'coreCollapse') {
        const distance = Math.max(
          6.2,
          getCoreAppearance(current.corePhase).frameWidth / camera.aspect
        );
        physics.cameraGoal.set(0, CORE_HOME.y + distance * 0.18, distance);
        physics.cameraTarget.copy(CORE_HOME);
      }
      const shake = current.reduceMotion
        ? 0
        : physics.cameraShake + physics.flash * 0.015;
      physics.cameraGoal.x += Math.sin(elapsed * 41) * shake;
      physics.cameraGoal.y += Math.cos(elapsed * 37) * shake;
      const cameraFollowRate = physics.coreImpactActive ? 9 : 5;
      camera.position.lerp(
        physics.cameraGoal,
        Math.min(1, delta * cameraFollowRate)
      );
      cameraLook.lerp(
        physics.cameraTarget,
        Math.min(1, delta * (physics.coreImpactActive ? 10 : 6))
      );
      camera.lookAt(cameraLook.x, cameraLook.y, cameraLook.z);

      const defeated = current.bossHp <= 0;
      const coreVisible =
        stageName !== 'coreCollapse' && (!defeated || stageName !== 'result');
      coreMesh.visible = coreVisible && heartBlend < 0.25;
      fracture.shell.visible = coreVisible;
      coreWire.visible = coreVisible;
      const laserProgress =
        physics.elapsed /
        ((getCoreLaserDuration(
          stageName,
          current.reduceMotion,
          current.coreAttackType
        ) || 1) /
          1000);
      heart.update({
        elapsed,
        evolution: heartBlend,
        meltdown: meltdownBlend,
        transition: transitionActive ? transitionProgress : -1,
        stage: stageName,
        progress: THREE.MathUtils.clamp(laserProgress, 0, 1),
        camera,
        reduceMotion: current.reduceMotion,
        visible: coreVisible && !defeated,
      });
      laser.update({
        stage: stageName,
        progress: laserProgress,
        origin: core.position,
        camera,
        corePhase: current.corePhase,
        outcome: current.coreAttackOutcome,
        attackType: current.coreAttackType,
        reduceMotion: current.reduceMotion,
      });
      enemyDieEffects.update({
        phase: current.corePhase,
        elapsed,
        camera,
        moving: ['enemySpit', 'enemyRolling'].includes(stageName),
        reduceMotion: current.reduceMotion,
        visible: enemyProjectile.visible,
      });
      playerStyleAura.update({
        position: playerProjectile.position,
        style: current.attackStyle,
        elapsed,
        delta,
        reduceMotion: current.reduceMotion,
        moving: true,
        visible: playerProjectile.visible,
      });
      physics.playerStrikeElapsed += delta * 1000;
      playerStrike.update({
        combat: [
          'playerRolling',
          'relics',
          'playerDone',
          'playerOutcome',
        ].includes(stageName)
          ? physics.playerStrike
          : null,
        elapsed: physics.playerStrikeElapsed,
        die: playerProjectile,
        camera,
        reduceMotion: current.reduceMotion,
      });
      camera.updateMatrixWorld();
      const hitPoint = coreHitPoint.copy(core.position);
      hitPoint.y += 0.6;
      hitPoint.project(camera);
      mount.style.setProperty(
        '--hit-x',
        THREE.MathUtils.clamp(hitPoint.x * 0.5 + 0.5, 0.2, 0.8) *
          mount.clientWidth +
          'px'
      );
      mount.style.setProperty(
        '--hit-y',
        THREE.MathUtils.clamp(-hitPoint.y * 0.5 + 0.5, 0.18, 0.7) *
          mount.clientHeight +
          'px'
      );
      hitPoint.copy(laser.endpoint).project(camera);
      mount.style.setProperty(
        '--player-hit-x',
        THREE.MathUtils.clamp(hitPoint.x * 0.5 + 0.5, 0.2, 0.8) *
          mount.clientWidth +
          'px'
      );
      mount.style.setProperty(
        '--player-hit-y',
        THREE.MathUtils.clamp(-hitPoint.y * 0.5 + 0.5, 0.18, 0.7) *
          mount.clientHeight +
          'px'
      );
      renderer.render(scene, camera);
    };

    const resize = () => {
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.zoom =
        window.innerWidth > 600 && !propsRef.current.decorative ? 1.12 : 1;
      camera.updateProjectionMatrix();
    };
    const stopObservingSize = observeElementSize(mount, resize);
    animate();

    return () => {
      cancelAnimationFrame(frame);
      stopObservingSize();
      enemyDieEffects.dispose();
      playerStyleAura.dispose();
      playerStrike.dispose();
      disposeSceneResources(scene);
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.width = 1;
      renderer.domElement.height = 1;
      if (mount.contains(renderer.domElement))
        mount.removeChild(renderer.domElement);
    };
  }, [notifyPlayerContact, forceFallback]);

  const coreAppearance = getCoreAppearance(corePhase);
  const coreAttackName =
    coreAttackType === 'massiveAttack'
      ? 'MASSIVE ATTACK'
      : coreAppearance.attackName;
  const stageLabel =
    stage === 'playerRolling'
      ? 'DIE BREACH // CORE LOCKED'
      : stage === 'playerOutcome'
      ? 'CORE IMPACT // DAMAGE CONFIRMED'
      : stage === 'relics'
      ? 'CORE IMPACT // DAMAGE CONFIRMED'
      : stage === 'bossCharge'
      ? `CORE LOCK // ${coreAttackName} CHARGING`
      : stage === 'bossFire'
      ? `${coreAttackName} // INBOUND`
      : ['bossImpact', 'bossOutcome'].includes(stage)
      ? coreAttackOutcome === 'protocol'
        ? 'LAST STAND // SIGNAL RESTORED'
        : coreAttackOutcome === 'decoy'
        ? 'FALSE SIGNAL // DECOY INTERCEPTED'
        : coreAttackOutcome === 'miss'
        ? 'CORE ATTACK // MISSED'
        : coreAttackOutcome === 'block'
        ? 'SHIELD HOLD // ATTACK BLOCKED'
        : 'SIGNAL BREACH // IMPACT CONFIRMED'
      : stage === 'enemyRolling'
      ? 'CORE RETALIATION INBOUND'
      : stage === 'enemySpit'
      ? 'CORE EJECTING COUNTER-DIE'
      : stage === 'enemyDone'
      ? 'IMPACT DETECTED'
      : stage === 'coreCollapse'
      ? 'CORE BISECTED // FINAL DETONATION'
      : stage === 'result'
      ? 'CLASH RESOLVED'
      : enraged
      ? 'MELTDOWN STATE'
      : 'CORE FREQUENCY ONLINE';
  const corePhaseLabel = coreAppearance.label;
  const playerDamageStage = [
    'playerOutcome',
    'bossWarning',
    'enemySpit',
    'bossSpit',
    'bossBoard',
    'bossCalculation',
    'bossOutcome',
    'coreCollapse',
    'result',
  ].includes(stage);
  const playerRollVisible =
    Number.isInteger(playerRoll) &&
    [
      'waiting',
      'relics',
      'playerDone',
      'playerOutcome',
      'bossWarning',
      'enemySpit',
      'bossSpit',
      'bossBoard',
      'bossCalculation',
      'enemyRolling',
      'enemyDone',
      'bossOutcome',
      'coreCollapse',
      'result',
    ].includes(stage);
  const coreAttackDamageVisible = [
    'bossOutcome',
    'coreCollapse',
    'result',
  ].includes(stage);
  const displayRoll = (value, visible) => (visible ? String(value) : '??');

  return (
    <div
      ref={mountRef}
      className={`coreboundCoreScene ${className} coreboundCoreScene-${stage} coreboundCoreScenePhase-${corePhase} ${
        enraged ? 'coreboundCoreSceneEnraged' : ''
      } ${reduceMotion ? 'coreboundCoreMotionReduced' : ''} ${
        webglAvailable ? '' : 'coreboundCoreSceneFallback'
      }`}
      role="group"
      aria-label={`Three-dimensional Glitch Core battle arena, ${corePhaseLabel}`}
      aria-hidden={decorative ? 'true' : undefined}
    >
      <span className="coreboundCoreSceneLabel">{stageLabel}</span>
      {playerHit && (
        <DamageHitSplat
          hitId={playerHit.id}
          damage={playerHit.damage}
          critical={playerHit.critical}
          blocked={playerHit.blocked}
          target="Core"
          reduceMotion={reduceMotion}
        />
      )}
      {incomingHit && (
        <DamageHitSplat
          hitId={incomingHit.id}
          damage={incomingHit.damage}
          target="You"
          incoming
          reduceMotion={reduceMotion}
        />
      )}
      {!webglAvailable &&
        ['enemySpit', 'enemyRolling', 'enemyDone'].includes(stage) && (
          <CoreDieFallback
            phase={corePhase}
            value={stage === 'enemyDone' ? formatRoll(bossRoll) : '?'}
            travelling={stage !== 'enemyDone'}
          />
        )}
      {showPhaseBadge && (
        <span
          className={`coreboundCorePhaseBadge coreboundCorePhaseBadge-${corePhase}`}
          aria-live="polite"
        >
          {corePhaseLabel}
        </span>
      )}
      {phaseAnnouncement && bossHp > 0 && stage !== 'coreCollapse' && (
        <div
          key={phaseAnnouncement}
          className={`coreboundCoreTransformation is-${phaseAnnouncement}`}
          role="status"
        >
          <span>{getCoreAppearance(phaseAnnouncement).transitionTell}</span>
          <strong>{getCoreAppearance(phaseAnnouncement).transitionName}</strong>
        </div>
      )}
      <div className="coreboundCoreSceneFooter">
        {children && (
          <div className="coreboundCoreSceneControls">{children}</div>
        )}
        {!['bossCharge', 'bossFire', 'bossImpact', 'bossOutcome'].includes(
          stage
        ) &&
          (playerRoll !== null || bossRoll !== null) && (
            <div
              className="coreboundCoreRollReadout"
              role="status"
              aria-live="polite"
            >
              <div
                className={`coreboundCoreRollValue ${
                  playerRollVisible ? 'is-revealed' : ''
                }`}
              >
                <span>
                  {playerDamageStage ? 'YOUR ATTACK DAMAGE' : 'YOUR ROLL'}
                </span>
                <strong>
                  {playerDamageStage &&
                  playerRollVisible &&
                  playerAttackShieldBlocked > 0 ? (
                    <span className="coreboundCoreDamageAdjusted">
                      <del>
                        {displayRoll(playerAttackDamageBeforeShield, true)}
                      </del>
                      <b>{displayRoll(playerRoll, true)}</b>
                    </span>
                  ) : (
                    displayRoll(playerRoll, playerRollVisible)
                  )}
                </strong>
              </div>
              <span className="coreboundCoreRollVs">VS</span>
              <div
                className={`coreboundCoreRollValue coreboundCoreRollValue-core ${
                  coreAttackDamageVisible ? 'is-revealed' : ''
                }`}
              >
                <span>CORE ATTACK DAMAGE</span>
                <strong>
                  {displayRoll(coreAttackDamage, coreAttackDamageVisible)}
                </strong>
              </div>
            </div>
          )}
      </div>
      {!webglAvailable && (
        <div className="coreboundCoreFallbackVisual" aria-hidden="true">
          {stage === 'coreCollapse' ? (
            <span className="coreboundCoreFallbackSplit">
              <i>⟐</i>
              <i>⟐</i>
            </span>
          ) : corePhase === 'fracture' && bossHp > 0 ? (
            <svg
              className="coreboundCoreFallbackShell"
              viewBox="-120 -110 240 240"
            >
              <path
                className="coreboundCoreFallbackArmor"
                d="M0-90 82-43 82 48 0 96-82 48-82-43Z M0-90 0-21 82-43 M0-21-82-43 M0-21 41 28 82 48 M41 28 0 96 M41 28-41 28-82 48 M-41 28 0-21"
              />
              <path
                className="coreboundCoreFallbackCracks"
                d="M-68-55-18-25-34 6 12 20-7 65 M53-61 28-15 56 14 28 47"
              />
            </svg>
          ) : corePhase !== 'containment' && bossHp > 0 ? (
            <svg
              className="coreboundCoreFallbackHeart"
              viewBox="-120 -110 240 240"
            >
              {corePhase === 'emergence' && (
                <path
                  className="coreboundCoreFallbackArmor"
                  d="M-47-74-100-47-110 44-65 78-85 12Z M47-74 100-47 110 44 65 78 85 12Z"
                />
              )}
              <g
                className="coreboundCoreFallbackTendrils"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="M-45-22C-82-95-117-65-93-23M45-22C82-95 117-65 93-23M-45 12C-108-18-118 37-88 46M45 12C108-18 118 37 88 46M-24 48C-83 60-75 110-50 100M24 48C83 60 75 110 50 100" />
              </g>
              <path
                className="coreboundCoreFallbackHeartBody"
                d="M0-35C-23-77-79-60-71-18-65 14-26 51 0 74 26 51 65 14 71-18 79-60 23-77 0-35Z"
              />
              <path
                className="coreboundCoreFallbackHeartVeins"
                d="M0-35-12-9 8 10-7 33 0 65M-12-9-47-27M8 10 48-7M-7 33-38 14"
              />
            </svg>
          ) : (
            <span>⟐</span>
          )}
          <strong>{corePhaseLabel}</strong>
          <small>
            {stage === 'enemyRolling'
              ? 'RETALIATION INBOUND'
              : stage === 'bossCharge'
              ? `${coreAttackName} CHARGING`
              : stage === 'bossFire'
              ? `${coreAttackName} INBOUND`
              : ['bossImpact', 'bossOutcome'].includes(stage)
              ? coreAttackOutcome === 'hit'
                ? 'SIGNAL DAMAGED'
                : coreAttackOutcome === 'protocol'
                ? 'LAST STAND // 1 HP'
                : coreAttackOutcome === 'decoy'
                ? 'HOLOGRAM HIT'
                : coreAttackOutcome === 'block'
                ? 'SHIELD HELD'
                : 'LASER MISSED'
              : stage === 'enemySpit'
              ? 'COUNTER-DIE EJECTING'
              : stage === 'coreCollapse'
              ? 'CORE SLICED // DETONATING'
              : 'BREACH CHANNEL READY'}
          </small>
        </div>
      )}
    </div>
  );
};

export default GlitchCoreScene;
